import React, { useCallback, useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';

import Row from 'components/Common/Row';
import { TOP_BAR_BOX_SHADOW_CSS } from 'components/ResourceTopBar';
import { buildShowCosts } from '../AllSets/routes';
import COSTS_SET_ROUTES from './routes';
import { DATA_MANAGER_TOP_ACTION_DIV_ID } from '../AllSets/constants';
import CostsShowContext from '../Costs/context/CostsShowContext';
import ActivityGridContext from '../Costs/context/ActivityGridContext';
import useConfirmation from '../DatasetDefinitions/DatasetsIndex/PerformanceDatasetWizard/useConfirmation';
import useIntervals from '../Costs/CostsShow/hooks/useIntervals';
import useWarningConfirmation from 'components/ConfirmationModals/hooks/useWarningConfirmation';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Typography from 'kingpin/atoms/Typography';
import Button from 'kingpin/atoms/Button';
import RolesContext from 'contexts/RolesContext';
import PERMISSIONS from 'permissionDefinitions';
import publishCostModel from '../Costs/api/publishCostModel';
import GqlClientContext from 'contexts/GqlClientContext';
import ToastContext from 'contexts/ToastContext';
import AnalyticsContext from 'contexts/AnalyticsContext';
import useDangerConfirmation from 'components/ConfirmationModals/hooks/useDangerConfirmation';
import useGetTMS from 'components/OrderDetailsTabs/hooks/useGetTMS';
import Tooltip from 'components/Tooltip';
import Icon from 'kingpin/atoms/Icon';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';

const TopNavWrapper = styled.div`
  ${TOP_BAR_BOX_SHADOW_CSS};
  padding: 0px 32px;
  padding-bottom: 8px;
  background-color: var(--color-bg-primary);
`;

const GoBackButton = styled(Row)`
  align-items: center;
  margin-right: 16px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const TopNavigator = () => {
  const navigate = useNavigate();
  const breadcrumbPrefix = 'Costs';

  const { selectedPortal } = useContext(PortalsContext);
  const name = 'Activity Based Costs';
  const { costModel, refreshModel } = useContext(CostsShowContext);
  const { currentPermissions } = useContext(RolesContext);
  const { client } = useContext(GqlClientContext);
  const { showToast } = useContext(ToastContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { discardChanges } = useContext(CostsShowContext);

  const hasPublishPermission = currentPermissions.includes(
    PERMISSIONS.DATA_MANAGEMENT.ACTIVITY_COSTS_PUBLISH,
  );

  const { pathname } = useLocation();
  const {
    saveChanges,
    hasUnsavedChanges,
    getMutatedCosts,
    setSavingError,
    getInvalidIntervals,
  } = useContext(ActivityGridContext);

  const { getConfirmationThat, DangerConfirmation } = useWarningConfirmation({
    title: 'Unsaved Changes',
    continueText: 'Close without saving',
  });

  const { orders } = useGetTMS();

  const goBack = useCallback(() => {
    if (pathname === COSTS_SET_ROUTES.buildActivityCostsEdit(selectedPortal)) {
      navigate(COSTS_SET_ROUTES.buildActivityCostsBase(selectedPortal));
      return;
    }

    if (!hasUnsavedChanges) {
      navigate(buildShowCosts(selectedPortal));
      return;
    }

    getConfirmationThat(
      'If you navigate away, all changes made will be lost.',
    ).then((isConfirmed) => {
      if (isConfirmed) {
        navigate(buildShowCosts(selectedPortal));
      }
    });
  }, [
    getConfirmationThat,
    hasUnsavedChanges,
    navigate,
    pathname,
    selectedPortal,
  ]);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);

  const { startDateToPeriod } = useIntervals({ activityCosts: costModel });

  const { getConfirmation, ConfirmationModal } = useConfirmation({
    title: 'Costs for the following intervals will be saved',
    bodyComponent: (
      <div style={{ marginBottom: '16px' }}>
        {getMutatedCosts()
          .map((cost) =>
            startDateToPeriod({
              startDate: cost.startDate,
              interval: costModel ? costModel.interval : 'month',
            }),
          )
          .map((interval) => (
            <div style={{ display: 'flex' }} key={interval.label}>
              <Typography.Body type="Body 12" key={interval.label}>
                {interval.label}
              </Typography.Body>
            </div>
          ))}
      </div>
    ),
    confirmText: 'Save',
    noMarginBottom: true,
  });

  const {
    getConfirmationThat: getPublishConfirmation,
    DangerConfirmation: PublishConfirmation,
  } = useDangerConfirmation({
    title: `Publishing the cost model will rewrite the ${orders[0]} dataset from ${moment(costModel?.startDate).format("MMM DD 'YY")}. Are you sure you want to Publish changes now?`,
    checkboxText: `I understand the impact on the ${orders[0]} dataset as a result of publishing the cost model`,
  });

  const handleSave = useCallback(async () => {
    const invalidIntervals = getInvalidIntervals();
    if (invalidIntervals.length > 0) {
      setSavingError({
        hasError: true,
        invalidIntervals,
      });
      return;
    }

    const isConfirmed = await getConfirmation();
    if (!isConfirmed) {
      return;
    }
    setIsSaving(true);
    await saveChanges();
    setIsSaving(false);
  }, [getConfirmation, getInvalidIntervals, saveChanges, setSavingError]);

  const isPublishButtonVisible =
    !!costModel && costModel.isDraft && hasPublishPermission;

  const onPublishClicked = useCallback(async () => {
    if (!costModel) {
      return;
    }
    const invalidIntervals = getInvalidIntervals();
    if (invalidIntervals.length > 0) {
      setSavingError({
        hasError: true,
        invalidIntervals,
      });
      return;
    }

    getPublishConfirmation('').then(async (isConfirmed) => {
      if (isConfirmed) {
        setIsPublishing(true);
        await publishCostModel({
          costModelId: costModel.id,
          client,
        });
        await refreshModel();
        showToast('Cost Model Published');
        trackEvent('Costs - Published', {
          costModel: costModel.modelType,
        });
        setIsPublishing(false);
      }
    });
  }, [
    client,
    costModel,
    getInvalidIntervals,
    getPublishConfirmation,
    refreshModel,
    setSavingError,
    showToast,
    trackEvent,
  ]);

  const openSettings = useCallback(() => {
    trackEvent('Costs - Settings opened', {
      costModel: costModel?.modelType,
    });
  }, [costModel, trackEvent]);

  return (
    <TopNavWrapper>
      <Row centerAlign spaceBetween style={{ marginTop: 10, marginBottom: 8 }}>
        <Row centerAlign spaceBetween>
          <Typography.Header type="H3">Data Manager</Typography.Header>
          <div id={DATA_MANAGER_TOP_ACTION_DIV_ID} />
        </Row>
        {costModel && (
          <Row centerAlign>
            <Link
              to={
                pathname ===
                COSTS_SET_ROUTES.buildActivityCostsEdit(selectedPortal)
                  ? COSTS_SET_ROUTES.buildActivityCostsBase(selectedPortal)
                  : hasUnsavedChanges
                    ? '#'
                    : COSTS_SET_ROUTES.buildActivityCostsEdit(selectedPortal)
              }
            >
              <Tooltip
                content={
                  hasUnsavedChanges
                    ? `You must ${costModel.isDraft ? 'Save Draft' : 'Update & Apply Costs'} or Discard Changes before you can edit settings`
                    : ''
                }
              >
                <Button
                  onClick={openSettings}
                  size="Small"
                  type="Tertiary"
                  label={
                    pathname ===
                    COSTS_SET_ROUTES.buildActivityCostsEdit(selectedPortal)
                      ? 'Go To Costs Input'
                      : 'Settings'
                  }
                  icon={
                    pathname ===
                    COSTS_SET_ROUTES.buildActivityCostsEdit(selectedPortal)
                      ? undefined
                      : 'settings-empty'
                  }
                  isDisabled={hasUnsavedChanges}
                />
              </Tooltip>
            </Link>
            {pathname !==
              COSTS_SET_ROUTES.buildActivityCostsEdit(selectedPortal) && (
              <>
                <div
                  style={{
                    width: '1px',
                    height: '28px',
                    backgroundColor: 'var(--color-border-secondary)',
                    marginRight: '24px',
                    marginLeft: '24px',
                  }}
                ></div>
                <div style={{ marginRight: '16px' }}>
                  <Button
                    type="Tertiary"
                    size="Small"
                    onClick={discardChanges}
                    label="Discard Changes"
                    isDisabled={!hasUnsavedChanges}
                  />
                </div>

                <div>
                  <Button
                    type="Primary"
                    size="Small"
                    onClick={handleSave}
                    testId={
                      !hasUnsavedChanges
                        ? undefined
                        : costModel.isDraft
                          ? 'Save Draft'
                          : 'Update & Apply Costs'
                    }
                    label={
                      costModel.isDraft ? 'Save Draft' : 'Update & Apply Costs'
                    }
                    isDisabled={!hasUnsavedChanges}
                    isLoading={isSaving}
                  />
                </div>

                {isPublishButtonVisible && (
                  <>
                    <div
                      style={{
                        width: '1px',
                        height: '28px',
                        backgroundColor: 'var(--color-border-secondary)',
                        marginRight: '24px',
                        marginLeft: '24px',
                      }}
                    ></div>
                    <Button
                      type="Primary"
                      size="Small"
                      onClick={onPublishClicked}
                      label={'Publish'}
                      isLoading={isPublishing}
                    />
                  </>
                )}
              </>
            )}
          </Row>
        )}
      </Row>
      <Row centerAlign style={{ marginBottom: '8px' }}>
        <div style={{ marginRight: 8 }}>
          <GoBackButton onClick={goBack}>
            <div style={{ marginRight: '8px', display: 'flex' }}>
              <Icon color={'var(--color-info-v2)'} icon={'arrow-left'} />
            </div>
            <Typography.Body
              type={'Button Text'}
              color={'var(--color-info-v2)'}
            >
              Back
            </Typography.Body>
          </GoBackButton>
        </div>
        <Typography.Body type="Body 12">
          {costModel
            ? `${breadcrumbPrefix} / ${name} (${costModel.dateField}) - Starting from ${costModel.startDate}`
            : `${breadcrumbPrefix} / ${name}`}
        </Typography.Body>
      </Row>
      {ConfirmationModal}
      <ModalTransition>{DangerConfirmation}</ModalTransition>
      <ModalTransition>{PublishConfirmation}</ModalTransition>
    </TopNavWrapper>
  );
};

export default TopNavigator;
